'use client';
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';

export type ViewAsPortal = 'teacher' | 'family';

export interface ViewAsProfile {
  portal: ViewAsPortal;
  profileId: string; // teacher.id for teacher portal, student_contacts.id for family portal
  profileName: string; // display name
  profileEmail?: string; // for family portal — the contact email used to scope data
  teacherId?: string; // for teacher portal — the teachers.id
}

interface ViewAsContextValue {
  viewAsProfile: ViewAsProfile | null;
  setViewAsProfile: (profile: ViewAsProfile | null) => void;
  exitViewAs: () => void;
  isViewingAs: boolean;
}

const ViewAsContext = createContext<ViewAsContextValue>({
  viewAsProfile: null,
  setViewAsProfile: () => {},
  exitViewAs: () => {},
  isViewingAs: false,
});

export const useViewAs = () => useContext(ViewAsContext);

export function ViewAsProvider({ children }: { children: React.ReactNode }) {
  const [viewAsProfile, setViewAsProfileState] = useState<ViewAsProfile | null>(null);
  const currentUserIdRef = useRef<string | null>(null);

  const setViewAsProfile = useCallback((profile: ViewAsProfile | null) => {
    setViewAsProfileState(profile);
  }, []);

  const exitViewAs = useCallback(() => {
    setViewAsProfileState(null);
  }, []);

  // Clear ViewAs state on sign-out OR when the authenticated user changes.
  // Impersonation is admin-initiated for a specific session — it must never
  // leak across sign-outs (a different user on the same browser could
  // otherwise inherit an admin's audit-mode identity).
  useEffect(() => {
    const supabase = createClient();
    let mounted = true;

    // Seed current user id (no reset required on first mount). A failing
    // getUser() must not throw an unhandled rejection — we just treat it as
    // no-session so the impersonation reset logic below still runs correctly
    // when a real sign-in event arrives.
    supabase.auth
      .getUser()
      .then(({ data }: { data: { user: { id: string } | null } }) => {
        if (mounted) currentUserIdRef.current = data?.user?.id ?? null;
      })
      .catch(() => {
        if (mounted) currentUserIdRef.current = null;
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
      if (!mounted) return;
      const nextUserId = session?.user?.id ?? null;
      const prevUserId = currentUserIdRef.current;

      if (event === 'SIGNED_OUT' || !session) {
        setViewAsProfileState(null);
        currentUserIdRef.current = null;
        return;
      }

      // If the user identity changed (including SIGNED_IN from null to a user),
      // reset impersonation. TOKEN_REFRESHED keeps the same user and should not
      // reset.
      if (prevUserId && nextUserId && prevUserId !== nextUserId) {
        setViewAsProfileState(null);
      } else if (!prevUserId && nextUserId && event === 'SIGNED_IN') {
        // Fresh sign-in after being signed out — clear any residual state.
        setViewAsProfileState(null);
      }
      currentUserIdRef.current = nextUserId;
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <ViewAsContext.Provider data-component-id="src/contexts/ViewAsContext.tsx:97:4" data-component-path="src/contexts/ViewAsContext.tsx" data-component-line="97" data-component-end-line="106" data-component-file="ViewAsContext.tsx" data-component-name="ViewAsContext.Provider" data-component-content="%7B%22elementName%22%3A%22ViewAsContext.Provider%22%7D"
      value={{
        viewAsProfile,
        setViewAsProfile,
        exitViewAs,
        isViewingAs: viewAsProfile !== null,
      }}
    >
      {children}
    </ViewAsContext.Provider>
  );
}
